export type SegmentTone =
  | "readyForRelease"
  | "readyForEditing"
  | "pending"
  | "complete"
  | "inProgress"
  | "remaining";

export type ProgressSegment = {
  tone: SegmentTone;
  size: number;
};

/**
 * Only the three states DEV-2697 names. `ready_for_translation` is deliberately not here: the bar
 * used to fold it into `readyForEditing`, which announced translation content as "Approved". It is
 * now counted nowhere, so the bar and its percentage describe the other three states only - see
 * docs/research-cms/tasks/0053.
 */
export type ReviewStatusCounts = {
  pending: number;
  readyForEditing: number;
  readyForRelease: number;
};

export type PracticeAreaCounts = {
  complete: number;
  inProgress: number;
  total: number;
};

export type ProgressSegmentsInput =
  | { variant: "review-status"; counts: ReviewStatusCounts }
  | { variant: "practice-area"; counts: PracticeAreaCounts };

function dropEmpty(segments: ProgressSegment[]): ProgressSegment[] {
  return segments.filter(segment => segment.size > 0);
}

function reviewStatusSegments(counts: ReviewStatusCounts): ProgressSegment[] {
  return dropEmpty([
    { tone: "readyForRelease", size: counts.readyForRelease },
    { tone: "readyForEditing", size: counts.readyForEditing },
    { tone: "pending", size: counts.pending },
  ]);
}

function practiceAreaSegments(counts: PracticeAreaCounts): ProgressSegment[] {
  const remaining = counts.total - counts.complete - counts.inProgress;
  return dropEmpty([
    { tone: "complete", size: counts.complete },
    { tone: "inProgress", size: counts.inProgress },
    { tone: "remaining", size: remaining },
  ]);
}

export function progressSegments(input: ProgressSegmentsInput): ProgressSegment[] {
  if (input.variant === "review-status") {
    return reviewStatusSegments(input.counts);
  }

  return practiceAreaSegments(input.counts);
}
